import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "antd";
import { useTranslation } from "react-i18next";

import { Body } from "../../../components/main-body/body";
import editIcon from "../../../assets/edit.svg";
import useNotification from "../../../components/notification/notification";
import { FormFieldName, FormTextarea } from "../../../components/form/form";
import ApiLifePointsManagement from "../../../api/lifepoints";
import "./life-points-description.css";

// Plain text, and short: the app lays this out itself rather than rendering markup.
// The backend refuses anything longer.
const MAX_DESCRIPTION_LENGTH = 2000;

/**
 * LF360-1872 — what a project tells its subjects LifePoints are.
 *
 * Its own screen under Administration, beside the other project settings, rather
 * than a section on the LifePoints table in Content: that table is the scoring
 * configuration and this is a sentence shown in the app. They are edited by
 * different people at different times.
 */
const LifePointsDescription = () => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();

  const [description, setDescription] = useState("");
  const [initialDescription, setInitialDescription] = useState("");
  const [isEdit, setEdit] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [isSaving, setSaving] = useState(false);
  // Bumped whenever what is on screen becomes the truth — editing starts, a save
  // lands. A load that began before that is answering about a version that has been
  // overtaken, and must not put itself into the field.
  const generation = useRef(0);
  // Whether a load has ever succeeded. Editing a field that was never filled would
  // let a save replace a description nobody has seen.
  const hasValue = useRef(false);
  // What the field holds right now, readable from inside a request that started
  // before the latest keystroke.
  const valueRef = useRef("");

  const setValue = (value) => {
    valueRef.current = value;
    setDescription(value);
  };

  const load = useCallback(async () => {
    const startedAt = generation.current;
    const { data } = await ApiLifePointsManagement.getDescription();
    const value = data.lifePointsDescription || "";

    hasValue.current = true;

    // Overtaken while it was in flight — by typing, or by a save that has already
    // stored something newer. Its answer is history, so it is dropped.
    if (generation.current !== startedAt) {
      return;
    }

    setValue(value);
    setInitialDescription(value);
  }, []);

  // Editing has to start from the real text. If the first load never arrived, ask
  // again now rather than opening an empty field over a description nobody has
  // seen — saving that would replace it.
  const startEditing = async () => {
    if (!hasValue.current) {
      try {
        await load();
      } catch (error) {
        openNotification({
          type: "error",
          message: (
            <p>
              {t(
                "lifepoints.description.loadFailed",
                "The description could not be loaded. Try again in a moment."
              )}
            </p>
          ),
        });
        return;
      }
    }

    generation.current += 1;
    setEdit(true);
  };

  useEffect(() => {
    // Nothing to say on failure: the field simply stays empty, and the way in
    // asks again.
    load().catch(() => {});
  }, [load]);

  const cancel = () => {
    setValue(initialDescription);
    setEdit(false);
    setHasChanges(false);
  };

  const save = async () => {
    const sent = valueRef.current;

    setSaving(true);

    try {
      const { data } = await ApiLifePointsManagement.updateDescription(sent);
      const value = data.lifePointsDescription || "";

      // Whatever the server echoes back describes the version that was sent. Typing
      // carried on while it was in flight, so putting that answer into the field
      // would delete the words written since — the baseline moves, the text stays.
      if (valueRef.current !== sent) {
        setInitialDescription(value);
        setHasChanges(true);
        return;
      }

      setValue(value);
      setInitialDescription(value);
      // What is stored is now what is on screen; an older load must not undo it.
      generation.current += 1;
      hasValue.current = true;
      setEdit(false);
      setHasChanges(false);

      openNotification({
        type: "success",
        message: <p>{t("lifepoints.description.saved", "Description saved")}</p>,
      });
    } catch (error) {
      openNotification({
        type: "error",
        message: (
          <p>
            {error?.response?.data?.error ||
              t("lifepoints.description.saveFailed", "Failed to save the description")}
          </p>
        ),
      });
    } finally {
      // Every path, including the early return that leaves newer text alone: without
      // it the button that started the save never becomes usable again.
      setSaving(false);
    }
  };

  return (
    <Body header={{ title: t("lifepoints.description.title", "LifePoints") }}>
      <div className="profile_form-container">
        <div className="profile_form__header">
          <span className="profile_form__header__title">
            {t("lifepoints.description.section", "Settings")}
          </span>

          <div style={{ height: 40 }}>
            {isEdit ? (
              <div className="profile-header__btns">
                <Button
                  style={{ height: 40, background: "rgba(108, 108, 137, 0.08)" }}
                  onClick={cancel}
                  // Cancelling mid-save would put the old copy back on screen while
                  // the new one was already on its way to being stored, leaving the
                  // two disagreeing with nothing to show it.
                  disabled={isSaving}
                >
                  {t("profile.btns.cancel")}
                </Button>
                <Button
                  type="primary"
                  style={{ marginLeft: 8, height: 40 }}
                  onClick={save}
                  disabled={!hasChanges || isSaving}
                >
                  {t("profile.btns.save")}
                </Button>
              </div>
            ) : (
              <img
                className="profile_form__header__edit-btn"
                src={editIcon}
                alt="edit"
                role="presentation"
                onClick={startEditing}
              />
            )}
          </div>
        </div>

        <div className="lifepoints-description">
          <div className="lifepoints-description__row">
            <div className="lifepoints-description__label">
              <FormFieldName text={t("lifepoints.description.label", "Description")} />
            </div>

            <div className="lifepoints-description__value">
              <FormTextarea
                value={description}
                readonly={!isEdit}
                maxLength={MAX_DESCRIPTION_LENGTH}
                autoSize={{ minRows: 3, maxRows: 12 }}
                placeholder="A short explanation of what LifePoints are"
                onChange={(event) => {
                  setValue(event.target.value);
                  setHasChanges(true);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </Body>
  );
};

export default LifePointsDescription;
